import React, { useRef, useState } from "react";
import BgComponent from "../Components/BgComponent";
import Logo from "../../../assets/image/logo/logo.png";
import RHFTextField from "../../../components/controls/RHFTextField";
import { Link, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import { useForm } from "react-hook-form";
import { object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Supabase from "../../../backend/client";
import MaskLoader from "../../../components/MaskLoader";
import { errorToaster } from "../../../util/toaster";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const SignupBeta = () => {
  const inputRef = useRef();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState("");

  const schema = object({
    email: string()
      .trim()
      .email("Please enter valid email address")
      .required()
      .label("Email")
      .nullable(),
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  React.useEffect(() => {
    document.title = "TRADEFOLIO - Join the Beta";
    const timer = setTimeout(() => setLoader(false), 1000);
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      clearTimeout(timer);
    };
  }, []);

  const onSubmit = async (data) => {
    setLoader(true);
    try {
      // Check if email already exists
      const { data: existingSignups } = await Supabase
        .from("beta_signups")
        .select("email")
        .eq("email", data.email);

      if (existingSignups?.length > 0) {
        setIsSuccess(true);
        setMessage("Thank you for your continued interest! We already have your email and we'll be in touch soon about the beta launch.");
        return;
      }

      // If email doesn't exist, proceed with insert
      const { error: insertError } = await Supabase
        .from("beta_signups")
        .insert([
          {
            email: data.email,
            first_name: null,
            last_name: null,
            why_interested: null,
            referral_source: document.referrer || 'direct'
          },
        ]);

      if (insertError) {
        if (insertError.code === '23505') { // PostgreSQL unique violation error code
          setIsSuccess(true);
          setMessage("Thank you for your continued interest! We already have your email and we'll be in touch soon about the beta launch.");
          return;
        }
        throw insertError;
      }

      setIsSuccess(true);
      setMessage("We'll be selecting users for our closed beta in the coming weeks. Keep an eye on your inbox for an invitation from the Tradefolio team.");
    } catch (error) {
      errorToaster("Something went wrong. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  return loader ? (
    <MaskLoader />
  ) : (
    <>
      <div className="flex flex-col min-h-[100vh] w-full">
        <div className="lg:hidden xs:mb-0">
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              margin: 3,
              color: "#000",
              position: "absolute",
              zIndex: 999,
            }}>
            <ArrowBackIcon />
          </IconButton>
        </div>
        <div className="flex flex-row w-full h-[100vh] flex-1">
          <div className="lg:flex flex-[0.40] xs:hidden">
            <BgComponent bgImg="bg-signup-bg">
              <p className="text-white text-[50px] font-bold absolute bottom-40 left-4">
                Putting the Power <br />
                Back Into Your Hands.
              </p>
            </BgComponent>
          </div>
          <div className="flex md:flex-[0.60] xs:flex-[0.80] flex-col lg:max-w-[550px] mx-auto xs:pt-[80px] md:pt-[40px] justify-center px-8">
            <div className="xs:py-4 xl:px-0">
              {isSuccess && (
                <div className="text-center p-4 bg-[#0061FC]/5 rounded-lg border border-[#0061FC]/10">
                  <CheckCircleIcon className="text-[#0061FC] mb-2" />
                  <p className="text-[#0061FC] font-medium mb-2">Thanks for signing up!</p>
                  <p className="text-[#7A86A1] text-sm">{message}</p>
                </div>
              )}
              {!isSuccess && (
                <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto lg:py-0">
                  <div className="w-full md:mt-0 sm:max-w-md xl:p-0 bg-white">
                    <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
                      <div className="flex justify-center items-center w-full">
                        <img
                          className="w-[170px] mb-8"
                          src={Logo}
                          alt="logo"
                        />
                      </div>
                      <p className="xs:text-[22px] md:text-[35px] font-bold font-sans">
                        Join the Beta
                      </p>
                      <p className="text-[#7A86A1] text-xs md:text-sm mb-8">
                        Simplifying the complex world of algorithmic trading
                      </p>
                      <div className="grid grid-cols-1 lg:grid-cols-2 xs:gap-[10px] md:gap-5 xs:pt-[15px]">
                        <div className="lg:col-span-2">
                          <RHFTextField
                            name="email"
                            id="email"
                            type="email"
                            fullWidth
                            placeholder="Your email"
                            className="px-[10px] xs:py-[5px] md:py-[10px] w-full text-[12px] text-[#7A86A1] border-[1px] border-[#7A86A1] rounded-lg "
                            isControlled={true}
                            control={control}
                            errorObj={errors}
                          />
                        </div>
                        <div className="lg:col-span-2 mt-2">
                          <button
                            onClick={handleSubmit(onSubmit)}
                            className="w-full bg-[#0061FC] text-white font-semibold xs:p-1 md:p-3 rounded-lg xs:text-[12] md:text-[14px] hover:bg-[#0052d6] transition-colors">
                            Join the Waitlist
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-between py-[20px] md:px-20 xs:px-4 text-[#000000] w-full border-t-[1px]">
          <div className="opacity-50 text-[10px] font-sans">
            &copy;2023 TRADEFOLIO Inc. All Rights Reserved
          </div>
          <div className="opacity-50 text-[10px] font-sans">
            <Link to="/privacy">
              <u>Privacy Policy</u>
            </Link>{" "}
            /{" "}
            <Link to="/terms">
              <u>User agreement</u>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignupBeta;
